import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import { Download, FileSpreadsheet, Pencil, Plus, Tags, Trash2, Upload } from "lucide-react";
import { Pagination, usePagination } from "@/components/Pagination";
import ConfirmDelete from "@/components/ConfirmDelete";

const EMPTY = { category_id: "", assigned_user_id: "", sku: "", product_name: "", description: "", currency: "IDR", price: "" };

async function downloadBlob(url, filename) {
  const res = await api.get(url, { responseType: "blob" });
  if (res.data?.type === "application/json") {
    const error = JSON.parse(await res.data.text());
    throw new Error(error.detail || "Download gagal");
  }
  const blob = new Blob([res.data], {
    type: res.headers["content-type"] || "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  });
  const href = window.URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = href;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(href);
}

export default function ProductCatalog({ embedded = false }) {
  const { user, isReadOnly } = useAuth();
  const [items, setItems] = useState([]);
  const [categories, setCategories] = useState([]);
  const [members, setMembers] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [editId, setEditId] = useState(null);
  const [pageItems, ctrl] = usePagination(items, 10);
  const [confirmDel, setConfirmDel] = useState(null);
  const [selectedIds, setSelectedIds] = useState([]);

  const load = async () => {
    const nextItems = (await api.get("/products")).data;
    setItems(nextItems);
    setSelectedIds((current) => current.filter((id) => nextItems.some((item) => item.id === id)));
  };
  const loadCategories = async () => setCategories((await api.get("/categories")).data);

  useEffect(() => {
    load();
    loadCategories();
    if (user?.role === "team_leader") {
      api.get("/users").then(({ data }) => setMembers(data.filter((item) => item.role === "member")));
    }
  }, [user?.role]);

  const resetForm = () => {
    setEditId(null);
    setForm(EMPTY);
  };

  const openNew = () => {
    resetForm();
    setOpen(true);
  };

  const openEdit = (it) => {
    setEditId(it.id);
    setForm({
      category_id: it.category_id || "",
      assigned_user_id: it.user_id || "",
      sku: it.sku || "",
      product_name: it.product_name || "",
      description: it.description || "",
      currency: it.currency || "IDR",
      price: it.price ?? "",
    });
    setOpen(true);
  };

  const submit = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        ...form,
        category_id: form.category_id || null,
        price: Number(form.price || 0),
      };
      if (editId) await api.patch(`/products/${editId}`, payload);
      else await api.post("/products", payload);
      toast.success(editId ? "Product diperbarui" : "Product ditambahkan");
      setOpen(false);
      resetForm();
      load();
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail));
    }
  };

  const remove = async (id) => {
    try {
      await api.delete(`/products/${id}`);
      toast.success("Product dihapus");
      load();
      return true;
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail));
      return false;
    }
  };

  const doConfirmDel = async () => {
    if (!confirmDel) return;
    if (confirmDel.ids) {
      try {
        const response = await api.post("/products/bulk-delete", { ids: confirmDel.ids });
        toast.success(`Product dihapus: ${response.data.deleted}`);
        setSelectedIds([]);
        setConfirmDel(null);
        load();
      } catch (err) {
        toast.error(formatApiError(err?.response?.data?.detail));
      }
      return;
    }
    if (await remove(confirmDel.id)) setConfirmDel(null);
  };

  const toggleSelected = (id, checked) => {
    setSelectedIds((current) => checked ? [...new Set([...current, id])] : current.filter((itemId) => itemId !== id));
  };

  const allSelected = items.length > 0 && selectedIds.length === items.length;

  const handleImport = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    try {
      const formData = new FormData();
      formData.append("file", file);
      const response = await api.post("/products/import", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success(`Import product berhasil: ${response.data.imported} baris diproses`);
      event.target.value = "";
      load();
    } catch (err) {
      event.target.value = "";
      toast.error(formatApiError(err?.response?.data?.detail || err?.message));
    }
  };

  const handleBulkPriceUpdate = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    try {
      const formData = new FormData();
      formData.append("file", file);
      const response = await api.post("/products/bulk-update", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const result = response.data;
      toast.success(`Harga diperbarui: ${result.updated}, SKU tidak ditemukan: ${result.not_found}`);
      event.target.value = "";
      load();
    } catch (err) {
      event.target.value = "";
      toast.error(formatApiError(err?.response?.data?.detail || err?.message));
    }
  };

  const downloadProductFile = async (url, filename) => {
    try {
      await downloadBlob(url, filename);
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail));
    }
  };

  return (
    <div className={embedded ? "space-y-6" : "space-y-8"} data-testid="product-catalog-page">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        {!embedded && <div>
          <div className="label-mini">Product</div>
          <h1 className="mt-1 font-heading text-4xl font-black tracking-tight">Product</h1>
        </div>}
        <div className="flex gap-2 flex-wrap">
          <Button variant="outline" className="rounded-sm" onClick={() => downloadProductFile("/products/template", "product-template.xlsx")} data-testid="download-product-template"><Download className="h-4 w-4 mr-2" />Template</Button>
          <Button variant="outline" className="rounded-sm" onClick={() => downloadProductFile("/products/export", "products.xlsx")} data-testid="export-product-excel"><FileSpreadsheet className="h-4 w-4 mr-2" />Export Excel</Button>
          <label className="inline-flex cursor-pointer items-center rounded-sm border border-border bg-background px-3 py-2 text-sm font-medium hover:bg-accent">
            <Upload className="h-4 w-4 mr-2" />Import Excel
            <input type="file" accept=".xlsx" className="hidden" onChange={handleImport} data-testid="import-product-file" />
          </label>
          {!isReadOnly && (
            <label className="inline-flex cursor-pointer items-center rounded-sm border border-border bg-background px-3 py-2 text-sm font-medium hover:bg-accent">
              <Tags className="h-4 w-4 mr-2" />Bulk Update Harga
              <input type="file" accept=".xlsx" className="hidden" onChange={handleBulkPriceUpdate} data-testid="bulk-update-product-file" />
            </label>
          )}
          {!isReadOnly && (
            <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) resetForm(); }}>
              <DialogTrigger asChild>
                <Button className="rounded-sm" onClick={openNew} data-testid="new-product-button"><Plus className="h-4 w-4 mr-2" />Product Baru</Button>
              </DialogTrigger>
              <DialogContent className="sm:max-w-2xl">
                <DialogHeader>
                  <DialogTitle>{editId ? "Edit Product" : "Product Baru"}</DialogTitle>
                </DialogHeader>
                <form onSubmit={submit} className="grid grid-cols-1 md:grid-cols-6 gap-4 pt-2">
                  <div className="md:col-span-2">
                    <Label className="label-mini">Category</Label>
                    <select
                      required
                      value={form.category_id}
                      onChange={(e) => setForm({ ...form, category_id: e.target.value })}
                      className="mt-2 flex h-10 w-full rounded-sm border border-border bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
                      data-testid="product-category-select"
                    >
                      <option value="">Pilih category</option>
                      {categories.map((cat) => (
                        <option key={cat.id} value={cat.id}>{cat.category_name}</option>
                      ))}
                    </select>
                  </div>
                  <div className="md:col-span-2">
                        {user?.role === "team_leader" && <>
                          <Label className="label-mini">PIC Member</Label>
                          <select
                            value={form.assigned_user_id}
                            onChange={(e) => setForm({ ...form, assigned_user_id: e.target.value })}
                            className="mt-2 flex h-10 w-full rounded-sm border border-border bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
                            data-testid="product-assignee-select"
                          >
                            <option value="">Belum ditentukan</option>
                            {members.map((member) => <option key={member.id} value={member.id}>{member.name}</option>)}
                          </select>
                        </>}
                      </div>
                      <div className="md:col-span-2">
                    <Label className="label-mini">SKU</Label>
                    <Input required value={form.sku} onChange={(e) => setForm({ ...form, sku: e.target.value })} data-testid="product-sku-input" />
                  </div>
                  <div className="md:col-span-2">
                    <Label className="label-mini">Product Name</Label>
                    <Input required value={form.product_name} onChange={(e) => setForm({ ...form, product_name: e.target.value })} data-testid="product-name-input" />
                  </div>
                  <div className="md:col-span-6">
                    <Label className="label-mini">Description</Label>
                    <Textarea rows={2} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="product-description-input" />
                  </div>
                  <div>
                    <Label className="label-mini">Currency</Label>
                    <select
                      value={form.currency}
                      onChange={(e) => setForm({ ...form, currency: e.target.value })}
                      className="mt-2 flex h-10 w-full rounded-sm border border-border bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
                      data-testid="product-currency-select"
                    >
                      <option value="IDR">IDR</option>
                      <option value="USD">USD</option>
                      <option value="EUR">EUR</option>
                    </select>
                  </div>
                  <div className="md:col-span-2">
                    <Label className="label-mini">Price</Label>
                    <Input type="number" step="0.01" min="0" value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} data-testid="product-price-input" />
                  </div>
                  <div className="md:col-span-6 flex gap-2">
                    <Button type="submit" className="rounded-sm" data-testid="save-product-button">{editId ? "Update" : "Tambah"}</Button>
                    <Button type="button" variant="outline" className="rounded-sm" onClick={() => { setOpen(false); resetForm(); }} data-testid="cancel-product-edit">Batal</Button>
                  </div>
                </form>
              </DialogContent>
            </Dialog>
          )}
          {!isReadOnly && items.length > 0 && (
            <div className="flex basis-full items-center gap-2 pt-1">
              <Button type="button" variant="outline" size="sm" className="rounded-sm" onClick={() => setSelectedIds(allSelected ? [] : items.map((item) => item.id))} data-testid="toggle-select-all-products">
                {allSelected ? "Deselect All" : "Select All"}
              </Button>
              {selectedIds.length > 0 && (
                <Button type="button" variant="destructive" size="sm" className="rounded-sm" onClick={() => setConfirmDel({ ids: selectedIds })} data-testid="bulk-delete-products">
                  <Trash2 className="h-4 w-4 mr-2" />Hapus {selectedIds.length} Product
                </Button>
              )}
            </div>
          )}
        </div>
      </div>

      <div className="card-tech p-0 overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-accent text-xs uppercase tracking-widest">
            <tr>
              <th className="w-12 px-6 py-3">
                {!isReadOnly && (
                  <Checkbox checked={allSelected} onCheckedChange={(checked) => setSelectedIds(checked ? items.map((item) => item.id) : [])} aria-label="Pilih semua product" data-testid="select-all-products" />
                )}
              </th>
              <th className="text-left px-6 py-3">Category</th>
              <th className="text-left px-6 py-3">SKU</th>
              <th className="text-left px-6 py-3">Product Name</th>
              <th className="text-left px-6 py-3">PIC</th>
              <th className="text-left px-6 py-3">Desc</th>
              <th className="text-left px-6 py-3">Currency</th>
              <th className="text-left px-6 py-3">Price</th>
              <th className="px-6 py-3"></th>
            </tr>
          </thead>
          <tbody>
            {items.length === 0 && <tr><td colSpan={9} className="px-6 py-10 text-center text-muted-foreground">Belum ada product.</td></tr>}
            {pageItems.map((it) => (
              <tr key={it.id} className="border-t border-border" data-testid={`product-row-${it.id}`}>
                <td className="px-6 py-3">
                  {!isReadOnly && <Checkbox checked={selectedIds.includes(it.id)} onCheckedChange={(checked) => toggleSelected(it.id, checked)} aria-label={`Pilih ${it.product_name}`} data-testid={`select-product-${it.id}`} />}
                </td>
                <td className="px-6 py-3">{it.category_name || "-"}</td>
                <td className="px-6 py-3 font-medium">{it.sku}</td>
                <td className="px-6 py-3">{it.product_name}</td>
                <td className="px-6 py-3">{it.pic_member_name || members.find((member) => member.id === it.user_id)?.name || "Belum ditentukan"}</td>
                <td className="px-6 py-3 text-muted-foreground">{it.description || "-"}</td>
                <td className="px-6 py-3">{it.currency || "IDR"}</td>
                <td className="px-6 py-3">{Number(it.price || 0).toLocaleString("id-ID")}</td>
                <td className="px-6 py-3 text-right whitespace-nowrap">
                  {!isReadOnly && (
                    <>
                      <Button size="sm" variant="ghost" onClick={() => openEdit(it)} data-testid={`edit-product-${it.id}`}><Pencil className="h-4 w-4" /></Button>
                      <Button size="sm" variant="ghost" onClick={() => setConfirmDel(it)} data-testid={`delete-product-${it.id}`}><Trash2 className="h-4 w-4" /></Button>
                    </>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        <Pagination ctrl={ctrl} testidPrefix="product-pg" />
      </div>

      <ConfirmDelete
        open={!!confirmDel}
        onOpenChange={(v) => !v && setConfirmDel(null)}
        onConfirm={doConfirmDel}
        entityLabel={confirmDel?.product_name}
        testIdPrefix="confirm-delete-product"
      />
    </div>
  );
}
